import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { X, Sparkle, Copy, DownloadSimple } from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Generic modal used to trigger any of the three Claude assistant endpoints
 * and display the returned markdown / json in a scrollable read-only box.
 *
 * Accepts:
 *  - endpoint: "generate-workout" | "exercise-description" | "progress-analysis"
 *  - payload: the request body to POST
 *  - title: displayed at the top of the modal
 *  - onClose: closes the dialog
 */
export default function AIAssistantModal({ endpoint, payload, title, onClose, clientId, onImported }) {
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState("");
  const [importing, setImporting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      try {
        const { data } = await axios.post(`${API}/ai/${endpoint}`, payload);
        if (cancelled) return;
        setText(data.markdown || data.raw || "");
      } catch (err) {
        console.error("[AIAssistantModal]", err);
        if (!cancelled) toast.error(err.response?.data?.detail || "Errore Claude");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [endpoint, payload]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(text).then(() => toast.success("Copiato"));
  };

  const handleImport = async () => {
    if (!clientId) {
      toast.error("Nessun cliente selezionato");
      return;
    }
    setImporting(true);
    try {
      const { data } = await axios.post(`${API}/ai/import-workout`, {
        client_id: clientId,
        raw_json: text,
      });
      toast.success(`Scheda importata: ${data.workouts_created} workout creati`);
      onImported?.(data);
      onClose?.();
      // Reload the client detail schede tab to reflect the new program.
      navigate(`/clients/${clientId}?tab=schede`, { replace: true });
      window.location.reload();
    } catch (err) {
      console.error("[AIAssistantModal] import failed", err);
      toast.error(err.response?.data?.detail || "Errore nell'importazione");
    } finally {
      setImporting(false);
    }
  };

  const canImport = endpoint === "generate-workout" && !!clientId && !loading && !!text;

  return (
    <div
      className="fixed inset-0 z-[75] flex items-center justify-center px-4"
      role="dialog"
      aria-modal="true"
      data-testid="ai-assistant-modal"
    >
      <button
        type="button"
        aria-label="Chiudi"
        onClick={onClose}
        className="absolute inset-0 bg-black/75 backdrop-blur-sm cursor-default"
      />
      <div className="relative w-full max-w-lg max-h-[85vh] bg-[#0F0F0F] border border-[#AF52DE]/40 rounded-2xl flex flex-col overflow-hidden shadow-[0_0_40px_rgba(175,82,222,0.15)]">
        <div className="px-5 py-3.5 border-b border-[#1A1A1A] flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            <Sparkle size={16} weight="fill" className="text-[#AF52DE] flex-shrink-0" />
            <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#AF52DE]">Claude AI</p>
              <h3
                className="text-base font-bold tracking-tight text-white uppercase truncate"
                style={{ fontFamily: "Barlow Condensed, sans-serif" }}
              >
                {title}
              </h3>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-[#1A1A1A] hover:bg-[#262626] text-zinc-400 hover:text-white flex items-center justify-center flex-shrink-0"
            aria-label="Chiudi"
            data-testid="ai-modal-close"
          >
            <X size={14} weight="bold" />
          </button>
        </div>

        <div className="overflow-y-auto px-5 py-4 flex-1">
          {loading ? (
            <div className="flex flex-col items-center py-8 gap-3">
              <div className="w-8 h-8 border-2 border-[#AF52DE] border-t-transparent rounded-full animate-spin" />
              <p className="text-xs text-zinc-500">Claude sta pensando…</p>
            </div>
          ) : (
            <pre
              className="whitespace-pre-wrap text-sm text-zinc-200 leading-relaxed font-sans"
              data-testid="ai-assistant-output"
            >
              {text || "Nessuna risposta."}
            </pre>
          )}
        </div>

        {!loading && text && (
          <div className="px-5 py-3 border-t border-[#1A1A1A] bg-[#0A0A0A] flex justify-end gap-2">
            <button
              type="button"
              onClick={copyToClipboard}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#1A1A1A] border border-[#262626] text-zinc-300 hover:text-white text-xs font-medium"
              data-testid="ai-modal-copy"
            >
              <Copy size={12} /> Copia
            </button>
            {canImport && (
              <button
                type="button"
                onClick={handleImport}
                disabled={importing}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gradient-to-r from-[#AF52DE] to-[#7B4BE6] hover:from-[#C46EEB] hover:to-[#9066EF] text-white text-xs font-bold disabled:opacity-50"
                data-testid="ai-modal-import"
              >
                <DownloadSimple size={12} weight="bold" />
                {importing ? "Importazione…" : "Importa nella scheda"}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
